import React, { useEffect, createRef, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';

import { Dates, getErrorMessage, useGetIntlDateFormatString } from 'helpers';
import { useFlags } from 'helpers/useFlags';
import { fetchSearch, getSearches } from 'services/items/redux';
import { transformItem, Item, deleteItem, deleteItems } from 'services/items';
import { fetchLocations } from 'services/locations';
import { useUrlQueryObject } from 'services/url';
import { fetchVendors } from 'services/vendors';
import { fetchUoms } from 'services/uoms';
import { fetchTaxRates } from 'services/taxRates';
import { Pagination } from 'services/search';
import { fetchTags, fetchTagsAPI, Tag } from 'services/tags';
import { ObjectType } from 'services/customFields';
import { fetchTrackingTypes } from 'services/settings/tracking';
import {
  showDownloadNotification,
  showLoadingNotification,
  showNotification,
} from 'services/api/notifications';
import { showProgressAlert } from 'services/alert/redux';
import {
  BackgroundAction,
  BackgroundType,
  startBackgroundExport,
  startBackgroundImport,
  useBackgroundTasks,
} from 'services/backgroundTasks';
import { PaperSlidingLayout } from 'ui/components/Paper/PaperSlidingLayout';
import { PageWithAdvancedSearch } from 'ui/components/Page/PageWithAdvancedSearch';
import { withSearchResults } from 'ui/components/Page/WithSearchResults';
import { ConfirmationModal } from 'ui/components/Modal/ConfirmationModal';
import { CustomFieldsModal } from 'ui/components/CustomFields/CustomFieldsModal';
import {
  clearModuleNavigation,
  ModuleNavigationType,
  removeModuleNavigation,
} from 'services/moduleNavigation';
import { fetchSettingsCompanies } from 'services/settings/company';
import { fetchSettingsSalesOrders } from 'services/settings/salesOrders';
import { fetchSettingsPurchaseOrders } from 'services/settings/purchaseOrders';

import { Routes } from '../../navigation';
import {
  ItemSearchResults,
  ItemDetailsCard,
  ItemsAdvancedSearch,
  ITEM_COLUMNS,
} from './components';
import {
  initialItemsFormValues,
  advancedSearchReduxActions,
  initialPagination,
  createDisplayValueMap,
  displayNameMap,
} from './consts';
import { ItemsPageProps, ItemsPageCmp } from './types';
import {
  ItemPageAction,
  ItemPageRowAction,
} from './components/ItemSearchResults/consts';
import { ItemUrlQueryObject } from './components/ItemDetailsCard/types';
import { logErrorCtx } from 'app/logging';
import { ErrorBoundary } from 'react-error-boundary';
import { FallbackUI } from 'ui/components/Modal/ErrorMessage/common';
import { ModuleNavigation } from 'app/components/AppBar/components';
import { fetchUsers } from 'services/user';
import { fetchUsers as fetchUsersV2 } from 'services/userV2';
import { useLocation, useNavigate } from 'react-router-dom';
import OneSchemaImporter from '@oneschema/react';
import { useOneSchemaToken } from '../../../../../serviceModule/hooks/useOneSchemaToken';

const ItemsPage: ItemsPageCmp = (props: ItemsPageProps) => {
  const flags = useFlags();
  const {
    token: oneSchemaJwt,
    loading: oneSchemaJwtLoading,
    error: oneSchemaJwtError,
    fetchToken: fetchOneSchemaJwt, // Function to manually refetch token if needed
    clientId: oneSchemaClientId,
    templateKeys: oneSchemaTemplateKeys,
  } = useOneSchemaToken({ enabled: flags.enableOneSchemaMaterialItemImport });
  const [
    showOneSchemaMaterialItemImporter,
    setShowOneSchemaMaterialItemImporter,
  ] = useState(false);
  const {
    searchState: searchResult,
    refreshSearchState: fetchSearchResult,
    isLoadingSearchState: isLoadingSearchResult,
    activeItemId,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const [, extendUrlQuery] = useUrlQueryObject<ItemUrlQueryObject>(
    navigate,
    location
  );
  const dispatch = useDispatch();

  const [clone, setClone] = useState(false);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteModalLoading, setDeleteModalLoading] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Item | null>();
  const [activeDate, setActiveDate] = useState<Dates>(Dates.DateCreated);
  const [customFieldsModalVisible, setCustomFieldsModalVisible] =
    useState(false);
  const [searchTags, setSearchTags] = useState<Tag[]>([]);
  const [bypassRouteLeavingGuard, setBypassRouteLeavingGuard] = useState(false);

  const { startFetching, startCsvFetching } = useBackgroundTasks();
  const intlFormatDate = useGetIntlDateFormatString();

  const hiddenItemsInput = createRef<HTMLInputElement>();
  const hiddenItemLocationsInput = createRef<HTMLInputElement>();
  const hiddenItemReorderPointInput = createRef<HTMLInputElement>();
  const hiddenVendorItemInput = createRef<HTMLInputElement>();

  // watch advanced search columns and fetch selected tags
  useEffect(() => {
    const tagIds = _.get(
      searchResult.advancedSearch.columns,
      ['tags.id'],
      null
    ) as number[];

    if (tagIds) {
      (async () => {
        try {
          const resTags = await fetchTagsAPI({}, tagIds);

          setSearchTags(resTags.data);
        } catch {
          //ignore Error
        }
      })();
    }
  }, [searchResult.advancedSearch.columns]);

  useEffect(() => {
    dispatch(clearModuleNavigation(ModuleNavigationType.Materials));

    return () => {
      dispatch(removeModuleNavigation());
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportData = useCallback(
    async (type: BackgroundType) => {
      showDownloadNotification();
      try {
        await startBackgroundExport(type);
        startFetching();
      } catch (e) {
        const error = e as Error;
        logErrorCtx('Error while starting background export', {
          error,
          stackTrace: error.stack,
          title: error.message,
          description: 'Export while exporting in itemsPage',
          component: 'ItemsPage',
        });
      }
    },
    [startFetching]
  );

  const importItems = useCallback(() => {
    hiddenItemsInput.current!.click();
  }, [hiddenItemsInput]);

  const importItemLocations = useCallback(() => {
    hiddenItemLocationsInput.current!.click();
  }, [hiddenItemLocationsInput]);

  const importReorderPoint = useCallback(() => {
    hiddenItemReorderPointInput.current!.click();
  }, [hiddenItemReorderPointInput]);

  const importVendorItems = useCallback(() => {
    hiddenVendorItemInput.current!.click();
  }, [hiddenVendorItemInput]);

  const escapeCsvCell = (cellData: any): string => {
    if (cellData == null) {
      return '';
    }
    const stringCellData = String(cellData);
    if (
      stringCellData.includes(',') ||
      stringCellData.includes('\n') ||
      stringCellData.includes('"')
    ) {
      return `"${stringCellData.replace(/"/g, '""')}"`;
    }
    return stringCellData;
  };

  const handleOneSchemaMaterialItemSuccess = async (data: any) => {
    setShowOneSchemaMaterialItemImporter(false);
    showLoadingNotification('Your Item import has been initiated.');
    startCsvFetching(); // Assumes startCsvFetching is available

    const materialItemImportName = BackgroundType.Item;

    try {
      // Define which property of 'col' to use for headers
      const headerNames =
        data.columns?.map(
          (col: any) =>
            col.template_column_name || // Primary for headers
            col.sheet_column_name ||
            col.template_column_key
        ) || [];

      // Define which property of 'col' to use for accessing data in records and for keysToProcess logic
      const columnAccessKeys =
        data.columns?.map(
          (col: any) =>
            col.template_column_key || // Primary for data access
            col.sheet_column_name
        ) || [];

      const headerRow = headerNames
        .map((headerName: string) => {
          return escapeCsvCell(headerName);
        })
        .join(',');

      const dataRows =
        data.records?.map((record: any) =>
          columnAccessKeys // Iterate using the access keys
            .map((accessKey: string) => {
              // accessKey is template_column_key or sheet_column_name
              if (!accessKey) {
                // Handle cases where a column might not have a key (e.g. if sheet_column_name was also null)
                return escapeCsvCell('');
              }
              const cellData = record[accessKey];
              let valueToUse = cellData;

              const keysToProcess = [
                'item_name',
                'item_description',
                'item_notes',
                'saleitem_name',
                // Add other template_column_keys here if they need special object/array handling
              ];

              // Use accessKey for the check
              if (keysToProcess.includes(accessKey)) {
                if (Array.isArray(cellData)) {
                  if (
                    cellData.length > 0 &&
                    typeof cellData[0] === 'object' &&
                    cellData[0] !== null
                  ) {
                    const firstObject = cellData[0];
                    if (typeof firstObject.value === 'string') {
                      valueToUse = firstObject.value;
                    } else if (typeof firstObject.name === 'string') {
                      valueToUse = firstObject.name;
                    } else {
                      console.warn(
                        `Column ${accessKey} is an array. First object lacks 'value' or 'name' string property: ${JSON.stringify(
                          firstObject
                        )}`
                      );
                    }
                  } else if (cellData.length === 0) {
                    console.warn(`Column ${accessKey} is an empty array.`);
                    valueToUse = '';
                  } else {
                    console.warn(
                      `Column ${accessKey} is an array, but first element is not a suitable object: ${JSON.stringify(
                        cellData[0]
                      )}`
                    );
                  }
                } else if (typeof cellData === 'object' && cellData !== null) {
                  if (typeof cellData.value === 'string') {
                    valueToUse = cellData.value;
                  } else if (typeof cellData.name === 'string') {
                    valueToUse = cellData.name;
                  } else {
                    console.warn(
                      `Column ${accessKey} is an object with no 'value' or 'name' string property: ${JSON.stringify(
                        cellData
                      )}`
                    );
                  }
                }
              }
              return escapeCsvCell(String(valueToUse));
            })
            .join(',')
        ) || [];
      const csvString = [headerRow, ...dataRows].join('\n');
      const fileName =
        data.sheet_metadata?.original_file_name || 'imported_items.csv';
      const csvFile = new File([csvString], fileName, { type: 'text/csv' });

      await startBackgroundImport(materialItemImportName, csvFile);
      dispatch(
        showProgressAlert(
          'Your Import for Items has been initiated. This may take a few minutes to complete.',
          materialItemImportName,
          BackgroundAction.Import
        )
      );
    } catch (error) {
      console.error('Error processing OneSchema data for items:', error);
      showNotification(
        'There was an error processing your item import. Please try again.',
        'error'
      );
    }
  };

  const handleHiddenInput = useCallback(
    (type: BackgroundType) =>
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
          return;
        }

        let notificationText = '';

        switch (type) {
          case BackgroundType.Item:
            notificationText = 'Item';
            break;
          case BackgroundType.ItemLocation:
            notificationText = 'Item Location';
            break;
          case BackgroundType.ReorderPoint:
            notificationText = 'Reorder Point';
            break;
          case BackgroundType.VendorItem:
            notificationText = 'Vendor Item';
            break;
        }

        const uploadedFile = event.target.files[0];
        showLoadingNotification(
          `Your ${notificationText} import has been initiated.`
        );

        startCsvFetching();

        event.target.value = '';
        try {
          await startBackgroundImport(type, uploadedFile);
          dispatch(
            showProgressAlert(
              'Your Import to Items has been initiated. This may take a few minutes to complete.',
              type,
              BackgroundAction.Import
            )
          );
        } catch (e) {
          const error = e as Error;
          const message = getErrorMessage(error);
          logErrorCtx('Error in startBackgroundImport', {
            error,
            stackTrace: error.stack,
            title: 'Background Import Failed',
            description: message,
            component: 'ItemsPage',
          });
          return;
        }
      },
    [dispatch]
  );

  const handleCloseItemInfoCard = () => extendUrlQuery({ activeId: null });

  const onAddNewClick = () => {
    extendUrlQuery({ activeId: -1 });
  };

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult({ pagination: newPagination });
  };

  const handleItemClick = useCallback(
    (id: number | null) => {
      setClone(false);
      extendUrlQuery({
        activeId: id,
      });
    },
    [extendUrlQuery]
  );

  const handleItemPageAction = useCallback(
    (action: ItemPageAction, value?: Dates) => {
      switch (action) {
        case ItemPageAction.ExportItems:
          exportData(BackgroundType.Item);
          break;
        case ItemPageAction.ImportItems:
          if (flags.enableOneSchemaMaterialItemImport) {
            setShowOneSchemaMaterialItemImporter(true);
          } else {
            importItems();
          }
          break;
        case ItemPageAction.ImportItemLocations:
          importItemLocations();
          break;
        case ItemPageAction.ExportItemLocations:
          exportData(BackgroundType.ItemLocation);
          break;
        case ItemPageAction.ChangeDate:
          setActiveDate(value!);
          break;
        case ItemPageAction.CustomFields:
          setCustomFieldsModalVisible(true);
          break;
        case ItemPageAction.ImportReorderPoint:
          importReorderPoint();
          break;
        case ItemPageAction.ExportReorderPoint:
          exportData(BackgroundType.ReorderPoint);
          break;
        case ItemPageAction.ImportVendorItems:
          importVendorItems();
          break;
        case ItemPageAction.ExportVendorItems:
          exportData(BackgroundType.VendorItem);
          break;
      }
    },
    [
      importItems,
      exportData,
      importItemLocations,
      importReorderPoint,
      importVendorItems,
      setShowOneSchemaMaterialItemImporter,
      flags,
    ]
  );

  const handleAction = useCallback(
    async (type: ItemPageRowAction, value: any) => {
      switch (type) {
        case ItemPageRowAction.Duplicate: {
          setClone(true);
          extendUrlQuery({
            activeId: value.id,
          });
          break;
        }
        case ItemPageRowAction.Delete: {
          setItemToDelete(value);
          break;
        }
      }
    },
    [extendUrlQuery]
  );

  const handleDeleteSingle = useCallback(async () => {
    setDeleteModalLoading(true);
    try {
      await deleteItem(itemToDelete!);
      await fetchSearchResult();
      setItemToDelete(null);
    } catch {
      // Ignore error
    }
    setDeleteModalLoading(false);
  }, [itemToDelete, fetchSearchResult]);

  const hideDeleteModal = useCallback(() => {
    setDeleteModalVisible(false);
  }, []);

  const onDeleteClicked = useCallback(() => setDeleteModalVisible(true), []);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleteModalLoading(true);
    const itemsToDelete = searchResult.items.filter((i) =>
      selectedItems.includes(i.id!)
    );
    try {
      await deleteItems(itemsToDelete);
      await fetchSearchResult();
      setSelectedItems([]);
    } catch {
      // Ignore error
    }

    hideDeleteModal();
    setDeleteModalLoading(false);
  }, [searchResult.items, selectedItems, fetchSearchResult, hideDeleteModal]);

  useEffect(() => {
    setBypassRouteLeavingGuard(false);
  }, [activeItemId]);

  return (
    <ErrorBoundary FallbackComponent={FallbackUI}>
      <ModuleNavigation />
      <PageWithAdvancedSearch
        detailCardColumns={ITEM_COLUMNS(activeDate)}
        initialFormValues={initialItemsFormValues}
        advancedSearchReduxActions={advancedSearchReduxActions}
        searchResult={searchResult}
        fetchSearchResult={fetchSearchResult}
        AdvancedSearchFieldsCmp={ItemsAdvancedSearch}
        displayValueMap={createDisplayValueMap(intlFormatDate, searchTags)}
        displayNameMap={displayNameMap}
        pageName="Items"
      >
        <PaperSlidingLayout shown={Boolean(activeItemId)}>
          <ItemSearchResults
            items={searchResult.items}
            columns={ITEM_COLUMNS(activeDate)}
            activeItemId={activeItemId}
            handleItemClick={handleItemClick}
            handleAddNewClick={onAddNewClick}
            isLoadingItems={isLoadingSearchResult}
            pagination={searchResult.pagination || initialPagination}
            onPaginationChange={handlePaginationChange}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            onAction={handleAction}
            onDeleteClicked={onDeleteClicked}
            onItemPageAction={handleItemPageAction}
          />
          <ItemDetailsCard
            itemId={activeItemId}
            fetchSearchResult={fetchSearchResult}
            onClose={handleCloseItemInfoCard}
            clone={clone}
            setClone={setClone}
            extendUrlQuery={extendUrlQuery}
            bypassRouteLeavingGuard={bypassRouteLeavingGuard}
            setBypassRouteLeavingGuard={setBypassRouteLeavingGuard}
          />
        </PaperSlidingLayout>
      </PageWithAdvancedSearch>
      <ConfirmationModal
        open={deleteModalVisible}
        title="Delete Items"
        body={`This will delete all selected Items and all related sale items. Are you sure?`}
        onCancelClicked={hideDeleteModal}
        onConfirmClicked={handleDeleteConfirm}
        isLoading={deleteModalLoading}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmButtonRed
      />
      {itemToDelete && (
        <ConfirmationModal
          open={true}
          title="Delete Item"
          body={`This will delete ${itemToDelete.name} and  all related sale items, are you sure?`}
          onCancelClicked={() => setItemToDelete(null)}
          onConfirmClicked={handleDeleteSingle}
          isLoading={deleteModalLoading}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          confirmButtonRed
        />
      )}
      <CustomFieldsModal
        open={customFieldsModalVisible}
        setVisible={setCustomFieldsModalVisible}
        module={ObjectType.Item}
      />

      {flags.enableOneSchemaMaterialItemImport &&
        showOneSchemaMaterialItemImporter &&
        oneSchemaJwtLoading && <p>Loading OneSchema token...</p>}
      {flags.enableOneSchemaMaterialItemImport &&
        showOneSchemaMaterialItemImporter &&
        oneSchemaJwtError && (
          <div>
            <p>Error loading OneSchema token: {oneSchemaJwtError}</p>
            <button onClick={() => fetchOneSchemaJwt()}>Retry</button>
          </div>
        )}
      {flags.enableOneSchemaMaterialItemImport &&
        showOneSchemaMaterialItemImporter &&
        oneSchemaJwt &&
        !oneSchemaJwtLoading &&
        !oneSchemaJwtError && (
          <OneSchemaImporter
            isOpen={showOneSchemaMaterialItemImporter}
            onRequestClose={() => setShowOneSchemaMaterialItemImporter(false)}
            clientId={oneSchemaClientId}
            userJwt={oneSchemaJwt}
            templateKey={oneSchemaTemplateKeys.items}
            importConfig={{ type: 'local' }}
            devMode={process.env.NODE_ENV === 'development'}
            className="oneschema-importer-iframe"
            style={{
              border: 'none',
              height: '100vh',
              width: '100vw',
              position: 'fixed',
              top: 0,
              left: 0,
              zIndex: 9999,
            }}
            onSuccess={handleOneSchemaMaterialItemSuccess}
            onError={(error) => {
              console.error('OneSchema Item Import Error:', error);
              showNotification(
                'OneSchema item import failed. Please try again.',
                { variant: 'error' }
              );
              setShowOneSchemaMaterialItemImporter(false);
            }}
          />
        )}

      <input
        type="file"
        ref={hiddenItemsInput}
        hidden
        onChange={handleHiddenInput(BackgroundType.Item)}
        accept=".csv"
      />
      <input
        type="file"
        ref={hiddenItemLocationsInput}
        hidden
        onChange={handleHiddenInput(BackgroundType.ItemLocation)}
        accept=".csv"
      />
      <input
        type="file"
        ref={hiddenItemReorderPointInput}
        hidden
        onChange={handleHiddenInput(BackgroundType.ReorderPoint)}
        accept=".csv"
      />
      <input
        type="file"
        ref={hiddenVendorItemInput}
        hidden
        onChange={handleHiddenInput(BackgroundType.VendorItem)}
        accept=".csv"
      />
    </ErrorBoundary>
  );
};

ItemsPage.route = Routes.ItemsPage;

export default withSearchResults<Item>(ItemsPage, {
  url: '/v1/items',
  expand: 'images,defaultUom,saleItems,bundleItems',
  dataAdapter: transformItem,
  quickSearchColumns: [
    'name',
    'upc',
    'sku',
    'itemType',
    'saleItems.name',
    'description',
  ],
  columns: [],
  fetchSearch,
  getSearches,
  initialPagination,
  rehydrationThunks: [
    fetchVendors,
    fetchUoms,
    fetchLocations,
    fetchTaxRates,
    fetchTrackingTypes,
    fetchSettingsCompanies,
    fetchSettingsSalesOrders,
    fetchSettingsPurchaseOrders,
    fetchTags,
    fetchUsers,
    fetchUsersV2,
  ],
});
